import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

function GiftPreferenceSurvey() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [matchData, setMatchData] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const [submittedRecipients, setSubmittedRecipients] = useState({});
  const [savingRecipient, setSavingRecipient] = useState(null);

  // Preferences state for each recipient
  const [preferencesByRecipient, setPreferencesByRecipient] = useState({});

  const interestOptions = [
    'Books & Reading',
    'Sports & Fitness',
    'Cooking & Food',
    'Technology & Gadgets',
    'Arts & Crafts',
    'Music',
    'Gaming',
    'Fashion & Beauty',
    'Home & Garden',
    'Travel',
    'Pets',
    'Other',
  ];

  useEffect(() => {
    if (!token) {
      setError('Invalid or missing token');
      setLoading(false);
      return;
    }

    fetchMatchData();
  }, [token]);

  const fetchMatchData = async () => {
    try {
      const response = await fetch(`/api/view-match?token=${token}`);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to load assignment data');
      }

      const data = await response.json();
      setMatchData(data);

      // Initialize preferences for each recipient
      const initialPreferences = {};
      const initialExpanded = {};

      data.assignments.forEach((recipient, index) => {
        initialPreferences[recipient.name] = {
          recipientAgeGroup: recipient.ageRange || '',
          recipientGender: recipient.sex || '',
          interests: [],
          hobbies: '',
          budgetRange: getBudgetRangeForRecipient(recipient, data.event),
        };
        // Expand first recipient by default
        if (index === 0) {
          initialExpanded[recipient.name] = true;
        }
      });

      setPreferencesByRecipient(initialPreferences);
      setExpandedSections(initialExpanded);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching match data:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  // Calculate budget range for a recipient based on event settings
  const getBudgetForRecipient = (recipient, eventData) => {
    if (!eventData) return null;

    if (eventData.sameBudgetForAll) {
      return eventData.budget;
    } else if (eventData.budgetsByType && recipient.ageRange) {
      return eventData.budgetsByType[recipient.ageRange];
    }

    return null;
  };

  // Convert budget number to budget range string
  const getBudgetRangeForRecipient = (recipient, eventData) => {
    const budget = getBudgetForRecipient(recipient, eventData);
    if (!budget) return '';

    const budgetNum = parseFloat(budget);

    // Map budget to predefined ranges
    if (budgetNum <= 20) return '10-20';
    if (budgetNum <= 30) return '20-30';
    if (budgetNum <= 50) return '30-50';
    if (budgetNum <= 75) return '50-75';
    if (budgetNum <= 100) return '75-100';
    return '100+';
  };

  const toggleSection = (recipientName) => {
    setExpandedSections(prev => ({
      ...prev,
      [recipientName]: !prev[recipientName]
    }));
  };

  const handleInterestToggle = (recipientName, interest) => {
    setPreferencesByRecipient(prev => ({
      ...prev,
      [recipientName]: {
        ...prev[recipientName],
        interests: prev[recipientName].interests.includes(interest)
          ? prev[recipientName].interests.filter(i => i !== interest)
          : [...prev[recipientName].interests, interest]
      }
    }));
  };

  const updatePreference = (recipientName, field, value) => {
    setPreferencesByRecipient(prev => ({
      ...prev,
      [recipientName]: {
        ...prev[recipientName],
        [field]: value
      }
    }));
  };

  const handleSubmitForRecipient = async (recipientName) => {
    setSavingRecipient(recipientName);

    try {
      const response = await fetch('/api/gift-preferences', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          recipientName,
          preferences: preferencesByRecipient[recipientName],
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to save preferences');
      }

      setSubmittedRecipients(prev => ({ ...prev, [recipientName]: true }));
      setSavingRecipient(null);

      // Collapse current section and expand next if available
      const recipientNames = matchData.assignments.map(r => r.name);
      const currentIndex = recipientNames.indexOf(recipientName);
      if (currentIndex < recipientNames.length - 1) {
        const nextRecipient = recipientNames[currentIndex + 1];
        setExpandedSections(prev => ({
          ...prev,
          [recipientName]: false,
          [nextRecipient]: true
        }));
      } else {
        // If last recipient, collapse this section
        setExpandedSections(prev => ({
          ...prev,
          [recipientName]: false
        }));
      }
    } catch (err) {
      console.error('Error saving preferences:', err);
      alert('Failed to save preferences. Please try again.');
      setSavingRecipient(null);
    }
  };

  const handleSkipAll = () => {
    // Mark all as "submitted" to show completion screen
    const allSubmitted = {};
    matchData.assignments.forEach(recipient => {
      allSubmitted[recipient.name] = true;
    });
    setSubmittedRecipients(allSubmitted);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-green-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mb-4"></div>
          <p className="text-gray-700 text-lg">Loading your assignments...</p>
        </div>
      </div>
    );
  }

  if (error && !token) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-green-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full text-center">
          <div className="text-6xl mb-4">❌</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Invalid Link</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-green-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full text-center">
          <div className="text-6xl mb-4">❌</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Error</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-6 rounded-lg transition"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const allSubmitted = matchData?.assignments.every(r => submittedRecipients[r.name]);
  const submittedCount = Object.keys(submittedRecipients).length;
  const totalCount = matchData?.assignments.length || 0;

  if (allSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-green-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full text-center">
          <div className="text-6xl mb-4">✅</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Thank You!</h1>
          <p className="text-gray-700 mb-6">
            Your preferences have been saved for {totalCount === 1 ? 'your recipient' : `all ${totalCount} recipients`}.
            We'll send you personalized gift suggestions via email soon!
          </p>
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6 text-left">
            <p className="text-sm text-yellow-800">
              <strong>Remember:</strong> You can unsubscribe from gift suggestion emails at any time
              by clicking the unsubscribe link in any email we send you.
            </p>
          </div>
          <p className="text-sm text-gray-500">You can now close this window.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-green-50 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-lg shadow-xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="text-5xl mb-4">🎁</div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">
              Help Us Personalize Your Suggestions
            </h1>
            <p className="text-gray-600 mb-4">
              You're buying gifts for <strong>{totalCount}</strong> {totalCount === 1 ? 'person' : 'people'}.
              Please help us understand each recipient better so we can suggest the perfect gifts!
            </p>
            {totalCount > 1 && (
              <div className="inline-flex items-center gap-2 bg-blue-50 px-4 py-2 rounded-lg">
                <span className="text-sm font-semibold text-blue-900">
                  Progress: {submittedCount} of {totalCount} completed
                </span>
              </div>
            )}
          </div>

          {error && (
            <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
              <p className="text-red-700">{error}</p>
            </div>
          )}

          {/* Recipients Accordion */}
          <div className="space-y-4 mb-6">
            {matchData?.assignments.map((recipient) => {
              const isExpanded = expandedSections[recipient.name];
              const isSubmitted = submittedRecipients[recipient.name];
              const isSaving = savingRecipient === recipient.name;
              const preferences = preferencesByRecipient[recipient.name] || {};
              const budget = getBudgetForRecipient(recipient, matchData.event);

              return (
                <div
                  key={recipient.name}
                  className={`border-2 rounded-lg overflow-hidden transition-all ${
                    isSubmitted ? 'border-green-500 bg-green-50' : 'border-gray-300'
                  }`}
                >
                  {/* Accordion Header */}
                  <button
                    onClick={() => toggleSection(recipient.name)}
                    className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">🎁</span>
                      <div className="text-left">
                        <h3 className="text-xl font-bold text-gray-800">{recipient.name}</h3>
                        <p className="text-sm text-gray-600">
                          {recipient.ageRange} {recipient.sex && recipient.sex !== 'Unknown' ? `• ${recipient.sex}` : ''}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {isSubmitted && (
                        <span className="flex items-center gap-1 text-green-700 font-semibold text-sm">
                          <span>✓</span> Saved
                        </span>
                      )}
                      <span className={`text-gray-500 transform transition-transform ${isExpanded ? 'rotate-90' : ''}`}>
                        ▶
                      </span>
                    </div>
                  </button>

                  {/* Accordion Content */}
                  {isExpanded && (
                    <div className="p-6 pt-0 border-t border-gray-200 bg-white">
                      <form
                        onSubmit={(e) => {
                          e.preventDefault();
                          handleSubmitForRecipient(recipient.name);
                        }}
                        className="space-y-6"
                      >
                        {/* Age Group - Display Only */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Recipient's Age Group
                          </label>
                          <div className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700">
                            {recipient.ageRange || 'Not specified'}
                          </div>
                          <p className="text-xs text-gray-500 mt-1">Pre-filled from event data</p>
                        </div>

                        {/* Budget - Display Only */}
                        {budget && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Suggested Budget
                            </label>
                            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                              <div className="text-2xl font-bold text-blue-900">${budget}</div>
                              <p className="text-xs text-blue-700 mt-1">Set by your event organizer</p>
                            </div>
                          </div>
                        )}

                        {/* Gender */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Recipient's Gender
                          </label>
                          <select
                            value={preferences.recipientGender}
                            onChange={(e) => updatePreference(recipient.name, 'recipientGender', e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                          >
                            <option value="">Select gender (optional)</option>
                            <option value="Male">Male</option>
                            <option value="Female">Female</option>
                            <option value="Other">Other</option>
                            <option value="Prefer not to say">Prefer not to say</option>
                          </select>
                        </div>

                        {/* Interests */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Interests (select all that apply)
                          </label>
                          <div className="grid grid-cols-2 gap-2">
                            {interestOptions.map((interest) => (
                              <label
                                key={interest}
                                className="flex items-center space-x-2 p-2 rounded hover:bg-gray-50 cursor-pointer"
                              >
                                <input
                                  type="checkbox"
                                  checked={preferences.interests.includes(interest)}
                                  onChange={() => handleInterestToggle(recipient.name, interest)}
                                  className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                                />
                                <span className="text-sm text-gray-700">{interest}</span>
                              </label>
                            ))}
                          </div>
                        </div>

                        {/* Hobbies */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Hobbies or Specific Interests
                          </label>
                          <textarea
                            value={preferences.hobbies}
                            onChange={(e) => updatePreference(recipient.name, 'hobbies', e.target.value)}
                            placeholder="E.g., loves gardening, collects vintage records, enjoys hiking..."
                            rows="3"
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                          />
                        </div>

                        {/* Budget Range Override */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Budget Range {budget ? '(Optional - Override if needed)' : ''}
                          </label>
                          <select
                            value={preferences.budgetRange}
                            onChange={(e) => updatePreference(recipient.name, 'budgetRange', e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                          >
                            <option value="">Select budget (optional)</option>
                            <option value="10-20">$10 - $20</option>
                            <option value="20-30">$20 - $30</option>
                            <option value="30-50">$30 - $50</option>
                            <option value="50-75">$50 - $75</option>
                            <option value="75-100">$75 - $100</option>
                            <option value="100+">$100+</option>
                          </select>
                        </div>

                        {/* Submit Button */}
                        <div className="pt-4">
                          <button
                            type="submit"
                            disabled={isSaving}
                            className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-6 rounded-lg transition transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                          >
                            {isSaving ? 'Saving...' : `Save Preferences for ${recipient.name}`}
                          </button>
                        </div>
                      </form>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Skip All Button */}
          <div className="text-center pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={handleSkipAll}
              className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-3 px-8 rounded-lg transition"
            >
              Skip All for Now
            </button>
          </div>

          {/* Privacy Note */}
          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>🔒 Privacy Note:</strong> This information is used only to personalize your
              gift suggestions and is handled in accordance with GDPR/CCPA regulations.
              You can unsubscribe anytime.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default GiftPreferenceSurvey;
